import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Mic, 
  ArrowRight, 
  Sparkles, 
  User, 
  Building, 
  Target,
  Plus,
  X,
  Loader2,
  Volume2,
  Gauge,
  GraduationCap,
  Briefcase,
  Trophy,
  AlertCircle,
  Monitor,
  Bookmark,
  Share2,
  Pencil,
  Trash2,
  Users,
  Save,
  Play,
  Pause
} from "lucide-react";
import GuidedTour, { hasSeenTour } from "@/components/GuidedTour";

const ROLEPLAY_TOUR_KEY = "pitch_perfect_roleplay_tour_seen";

const ROLEPLAY_TOUR_STEPS = [
  {
    target: '[data-testid="new-roleplay-page"]',
    title: "Create Your Roleplay",
    description: "This is where you set up a practice scenario. Choose from presets, saved templates, or build a custom one from scratch.",
    position: "bottom",
    icon: Sparkles,
  },
  {
    target: '[data-testid="presets-tab"]',
    title: "Quick Start with Presets",
    description: "Pick from ready-made scenarios organized by difficulty — from friendly training calls to nightmare-mode cold calls.",
    position: "bottom",
    icon: GraduationCap,
    switchTab: "presets",
  },
  {
    target: '[data-testid="templates-tab"]',
    title: "Your Saved Templates",
    description: "Templates you've saved or ones shared by your team appear here. Click any card to load it instantly.",
    position: "bottom",
    icon: Bookmark,
    switchTab: "templates",
  },
  {
    target: '[data-testid="custom-tab"]',
    title: "Build a Custom Scenario",
    description: "Full control — set the scenario type, client personality, industry, and more. This is where the magic happens.",
    position: "bottom",
    icon: Sparkles,
    switchTab: "custom",
  },
  {
    target: '[data-testid="scenario-name-input"]',
    title: "Name Your Scenario",
    description: "Give it a descriptive name like 'Cold Call - Tech Startup CEO'. This helps you find it later in your history.",
    position: "bottom",
    icon: Pencil,
    switchTab: "custom",
  },
  {
    target: '[data-testid="voice-shimmer"]',
    title: "Choose a Voice",
    description: "Pick the AI client's voice. Each has a different tone — from warm and friendly to deep and authoritative.",
    position: "top",
    icon: Volume2,
    switchTab: "custom",
  },
  {
    target: '[data-testid="difficulty-medium"]',
    title: "Set the Difficulty",
    description: "Easy mode is great for practice. Extreme mode simulates hostile prospects who want to hang up immediately.",
    position: "top",
    icon: Gauge,
    switchTab: "custom",
  },
  {
    target: '[data-testid="objection-input"]',
    title: "Add Specific Objections",
    description: "Type objections you want to practice handling. The AI client will raise these during the call.",
    position: "top",
    icon: Target,
    switchTab: "custom",
  },
  {
    target: '[data-testid="screen-sharing-toggle"]',
    title: "Screen Sharing Mode",
    description: "Enable this if you're sharing your screen on a video call. It switches to push-to-talk to avoid echo.",
    position: "top",
    icon: Monitor,
    switchTab: "custom",
  },
  {
    target: '[data-testid="save-template-btn"]',
    title: "Save & Start!",
    description: "Save your setup as a template to reuse later, or hit Start Roleplay to begin your AI voice call right away.",
    position: "top",
    icon: Mic,
    switchTab: "custom",
  },
];

const PERSONALITIES = [
  { value: "skeptical", label: "Skeptical", desc: "Questions everything, needs proof" },
  { value: "enthusiastic", label: "Enthusiastic", desc: "Open and excited, easy to engage" },
  { value: "busy", label: "Busy Executive", desc: "Limited time, get to the point" },
  { value: "analytical", label: "Analytical", desc: "Data-driven, needs numbers" },
  { value: "friendly", label: "Friendly", desc: "Warm and conversational" },
];

const INDUSTRIES = [
  { value: "tech", label: "Technology" },
  { value: "retail", label: "Retail" },
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "hospitality", label: "Hospitality" },
  { value: "education", label: "Education" },
  { value: "real_estate", label: "Real Estate" },
  { value: "home_services", label: "Home Services" },
];

const COMPANY_SIZES = [
  { value: "startup", label: "Startup (1-10)" },
  { value: "smb", label: "SMB (11-100)" },
  { value: "midmarket", label: "Mid-Market (101-1000)" },
  { value: "enterprise", label: "Enterprise (1000+)" },
];

const SCENARIO_TYPES = [
  { value: "cold_call", label: "Cold Call", desc: "First contact with a prospect" },
  { value: "discovery", label: "Discovery Call", desc: "Understanding needs and qualifying" },
  { value: "upsell", label: "Upsell", desc: "Selling additional services to existing client" },
  { value: "objection_handling", label: "Objection Handling", desc: "Overcoming specific objections" },
  { value: "closing", label: "Closing Call", desc: "Final push to close the deal" },
];

const VOICE_OPTIONS = [
  { value: "cedar", label: "Cedar", desc: "Male, natural & grounded (new)", gender: "male" },
  { value: "marin", label: "Marin", desc: "Female, modern & crisp (new)", gender: "female" },
  { value: "shimmer", label: "Shimmer", desc: "Female, warm & friendly", gender: "female" },
  { value: "coral", label: "Coral", desc: "Female, professional", gender: "female" },
  { value: "alloy", label: "Alloy", desc: "Neutral, balanced", gender: "neutral" },
  { value: "echo", label: "Echo", desc: "Male, clear & direct", gender: "male" },
  { value: "ash", label: "Ash", desc: "Male, deep & authoritative", gender: "male" },
  { value: "verse", label: "Verse", desc: "Male, expressive", gender: "male" },
];

const DIFFICULTY_LEVELS = [
  { 
    value: "easy", 
    label: "Easy", 
    desc: "Friendly client, minimal objections",
    color: "text-green-500",
    bgColor: "bg-green-500/10"
  },
  { 
    value: "medium", 
    label: "Medium", 
    desc: "Realistic client, moderate challenge",
    color: "text-yellow-500",
    bgColor: "bg-yellow-500/10"
  },
  { 
    value: "hard", 
    label: "Hard", 
    desc: "Tough client, many objections",
    color: "text-orange-500",
    bgColor: "bg-orange-500/10"
  },
  { 
    value: "extreme", 
    label: "Extreme", 
    desc: "Hostile, ready to hang up",
    color: "text-red-500",
    bgColor: "bg-red-500/10"
  },
];

export default function NewRoleplay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const retryScenario = location.state?.scenarioSettings;
  const [presets, setPresets] = useState([]);
  const [myTemplates, setMyTemplates] = useState([]);
  const [teamTemplates, setTeamTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startingSession, setStartingSession] = useState(false);
  const [screenSharingMode, setScreenSharingMode] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: null });
  const [deleting, setDeleting] = useState(false);
  const defaultTab = retryScenario ? "custom" : "custom";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showTour, setShowTour] = useState(false);

  // Voice preview state
  const [previewVoice, setPreviewVoice] = useState(null); // currently playing voice
  const [previewLoading, setPreviewLoading] = useState(null); // voice currently loading
  const previewAudioRef = useRef(null);

  const handleVoicePreview = useCallback(async (voiceValue) => {
    // Toggle off if already playing this one
    if (previewVoice === voiceValue && previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setPreviewVoice(null);
      return;
    }
    // Stop any existing playback
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
      setPreviewVoice(null);
    }
    setPreviewLoading(voiceValue);
    try {
      const audio = new Audio(`${API}/voice/preview/${voiceValue}`);
      audio.crossOrigin = "use-credentials";
      previewAudioRef.current = audio;
      audio.onended = () => {
        if (previewAudioRef.current === audio) {
          previewAudioRef.current = null;
          setPreviewVoice(null);
        }
      };
      audio.onerror = () => {
        if (previewAudioRef.current === audio) {
          previewAudioRef.current = null;
          setPreviewVoice(null);
          setPreviewLoading(null);
          toast.error("Couldn't play preview. Try again in a moment.");
        }
      };
      audio.onplaying = () => {
        setPreviewLoading(null);
        setPreviewVoice(voiceValue);
      };
      await audio.play();
    } catch (err) {
      console.error("voice preview play error:", err);
      previewAudioRef.current = null;
      setPreviewVoice(null);
      setPreviewLoading(null);
      toast.error("Couldn't play preview");
    }
  }, [previewVoice]);

  // Stop preview on unmount
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
    };
  }, []);
  
  const [formData, setFormData] = useState(() => {
    if (retryScenario) {
      return {
        name: retryScenario.name || "",
        scenario_type: retryScenario.scenario_type || "cold_call",
        client_personality: retryScenario.client_personality || "skeptical",
        industry: retryScenario.industry || "tech",
        company_size: retryScenario.company_size || "smb",
        specific_objections: retryScenario.specific_objections || [],
        goals: retryScenario.goals || [],
        context: retryScenario.context || "",
        voice: retryScenario.voice || "shimmer",
        difficulty: retryScenario.difficulty || "medium",
        custom_ai_name: retryScenario.custom_ai_name || "",
        custom_business_name: retryScenario.custom_business_name || ""
      };
    }
    return {
      name: "",
      scenario_type: "cold_call",
      client_personality: "skeptical",
      industry: "tech",
      company_size: "smb",
      specific_objections: [],
      goals: [],
      context: "",
      voice: "shimmer",
      difficulty: "medium",
      custom_ai_name: "",
      custom_business_name: ""
    };
  });

  const [newObjection, setNewObjection] = useState("");
  const [newGoal, setNewGoal] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [presetsRes, myTplRes, teamTplRes] = await Promise.all([
          fetch(`${API}/presets/scenarios`, { credentials: "include" }),
          fetch(`${API}/templates`, { credentials: "include" }),
          fetch(`${API}/templates/team`, { credentials: "include" })
        ]);
        if (presetsRes.ok) setPresets(await presetsRes.json());
        if (myTplRes.ok) setMyTemplates(await myTplRes.json());
        if (teamTplRes.ok) setTeamTemplates(await teamTplRes.json());
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
    if (retryScenario) {
      toast.success("Scenario reloaded", { description: `Ready to retry: ${retryScenario.name}` });
    }
  }, [retryScenario]);

  // Trigger roleplay tour for first-time visitors
  useEffect(() => {
    if (!retryScenario && !hasSeenTour(ROLEPLAY_TOUR_KEY)) {
      const timer = setTimeout(() => setShowTour(true), 600);
      return () => clearTimeout(timer);
    }
  }, [retryScenario]);

  const handleBeforeStep = useCallback((stepIdx, step) => {
    if (step.switchTab) {
      setActiveTab(step.switchTab);
    }
  }, []);

  const handleAddObjection = () => {
    if (newObjection.trim() && !formData.specific_objections.includes(newObjection.trim())) {
      setFormData(prev => ({
        ...prev,
        specific_objections: [...prev.specific_objections, newObjection.trim()]
      }));
      setNewObjection("");
    }
  };

  const handleRemoveObjection = (objection) => {
    setFormData(prev => ({
      ...prev,
      specific_objections: prev.specific_objections.filter(o => o !== objection)
    }));
  };

  const handleAddGoal = () => {
    if (newGoal.trim() && !formData.goals.includes(newGoal.trim())) {
      setFormData(prev => ({
        ...prev,
        goals: [...prev.goals, newGoal.trim()]
      }));
      setNewGoal("");
    }
  };

  const handleRemoveGoal = (goal) => {
    setFormData(prev => ({
      ...prev,
      goals: prev.goals.filter(g => g !== goal)
    }));
  };

  const handleSelectPreset = (preset) => {
    setFormData({
      name: preset.name,
      scenario_type: preset.scenario_type,
      client_personality: preset.client_personality,
      industry: preset.industry,
      company_size: preset.company_size,
      specific_objections: preset.specific_objections || [],
      goals: preset.goals || [],
      context: preset.context || "",
      voice: preset.voice || "shimmer",
      difficulty: preset.difficulty || "medium"
    });
    toast.success("Preset loaded", { description: preset.name });
  };

  const handleLoadTemplate = (template) => {
    setFormData({
      name: template.name || "",
      scenario_type: template.scenario_type || "cold_call",
      client_personality: template.client_personality || "skeptical",
      industry: template.industry || "tech",
      company_size: template.company_size || "smb",
      specific_objections: template.specific_objections || [],
      goals: template.goals || [],
      context: template.context || "",
      voice: template.voice || "shimmer",
      difficulty: template.difficulty || "medium",
      custom_ai_name: template.custom_ai_name || "",
      custom_business_name: template.custom_business_name || ""
    });
    toast.success("Template loaded", { description: template.name });
  };

  const handleSaveAsTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a scenario name first");
      return;
    }
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API}/templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        const tpl = await res.json();
        setMyTemplates(prev => [tpl, ...prev]);
        toast.success("Template saved!", { description: formData.name });
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleShare = async (templateId) => {
    try {
      const res = await fetch(`${API}/templates/${templateId}/share`, {
        method: "PUT", credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        setMyTemplates(prev => prev.map(t =>
          t.template_id === templateId ? { ...t, is_shared: data.is_shared } : t
        ));
        toast.success(data.is_shared ? "Template shared with team" : "Template unshared");
      }
    } catch (error) {
      toast.error("Failed to update sharing");
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteDialog.id) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/templates/${deleteDialog.id}`, {
        method: "DELETE", credentials: "include"
      });
      if (res.ok) {
        setMyTemplates(prev => prev.filter(t => t.template_id !== deleteDialog.id));
        setTeamTemplates(prev => prev.filter(t => t.template_id !== deleteDialog.id));
        toast.success("Template deleted");
      } else {
        const err = await res.json();
        toast.error("Failed to delete", { description: err.detail });
      }
    } catch (error) {
      toast.error("Failed to delete template");
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, id: null, name: null });
    }
  };

  const handleStartRoleplay = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a scenario name");
      return;
    }

    setStartingSession(true);

    try {
      // First create the scenario
      const scenarioRes = await fetch(`${API}/scenarios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });

      if (!scenarioRes.ok) {
        throw new Error("Failed to create scenario");
      }

      const scenario = await scenarioRes.json();

      // Navigate to real-time voice roleplay with scenario info
      toast.success("Starting voice call...");
      navigate(`/roleplay/active/new`, {
        state: { 
          scenarioId: scenario.scenario_id,
          scenario: scenario,
          screenSharingMode: screenSharingMode
        }
      });
    } catch (error) {
      console.error("Failed to start roleplay:", error);
      toast.error("Failed to start roleplay", { description: error.message });
    } finally {
      setStartingSession(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="new-roleplay-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold">New Roleplay Session</h1>
        <p className="text-muted-foreground mt-1">Configure your practice scenario</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="presets" data-testid="presets-tab">Presets</TabsTrigger>
          <TabsTrigger value="templates" data-testid="templates-tab">Templates</TabsTrigger>
          <TabsTrigger value="custom" data-testid="custom-tab">Custom</TabsTrigger>
        </TabsList>

        {/* Presets Tab */}
        <TabsContent value="presets" className="mt-6 space-y-8">
          {/* Training Mode - Easy */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Training Mode</h3>
                <p className="text-sm text-muted-foreground">Perfect for beginners • Friendly clients • Minimal objections</p>
              </div>
              <Badge className="bg-green-500/10 text-green-500 ml-auto">Easy</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {presets.filter(p => p.category === "training").map((preset) => (
                <Card 
                  key={preset.id} 
                  className="cursor-pointer hover:border-green-500/50 transition-colors border-l-4 border-l-green-500"
                  onClick={() => handleSelectPreset(preset)}
                  data-testid={`preset-${preset.id}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{preset.name.replace("Training: ", "")}</CardTitle>
                    <CardDescription className="text-xs line-clamp-2">{preset.context}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.scenario_type.replace(/_/g, " ")}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.client_personality}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Real World - Medium */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Real World</h3>
                <p className="text-sm text-muted-foreground">Realistic scenarios • Moderate challenge • Common objections</p>
              </div>
              <Badge className="bg-yellow-500/10 text-yellow-500 ml-auto">Medium</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {presets.filter(p => p.category === "realworld").map((preset) => (
                <Card 
                  key={preset.id} 
                  className="cursor-pointer hover:border-yellow-500/50 transition-colors border-l-4 border-l-yellow-500"
                  onClick={() => handleSelectPreset(preset)}
                  data-testid={`preset-${preset.id}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{preset.name.replace("Real World: ", "")}</CardTitle>
                    <CardDescription className="text-xs line-clamp-2">{preset.context}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.scenario_type.replace(/_/g, " ")}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.client_personality}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Expert Challenge - Hard */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Expert Challenge</h3>
                <p className="text-sm text-muted-foreground">For experienced reps • Tough negotiations • Multiple objections</p>
              </div>
              <Badge className="bg-orange-500/10 text-orange-500 ml-auto">Hard</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {presets.filter(p => p.category === "expert").map((preset) => (
                <Card 
                  key={preset.id} 
                  className="cursor-pointer hover:border-orange-500/50 transition-colors border-l-4 border-l-orange-500"
                  onClick={() => handleSelectPreset(preset)}
                  data-testid={`preset-${preset.id}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{preset.name.replace("Expert: ", "")}</CardTitle>
                    <CardDescription className="text-xs line-clamp-2">{preset.context}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.scenario_type.replace(/_/g, " ")}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.client_personality}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Nightmare Mode - Extreme */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-600/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Nightmare Mode</h3>
                <p className="text-sm text-muted-foreground">Hostile prospects • Quick hang-ups • Survival training</p>
              </div>
              <Badge className="bg-red-600/10 text-red-600 ml-auto">Extreme</Badge>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {presets.filter(p => p.category === "nightmare").map((preset) => (
                <Card 
                  key={preset.id} 
                  className="cursor-pointer hover:border-red-600/50 transition-colors border-l-4 border-l-red-600"
                  onClick={() => handleSelectPreset(preset)}
                  data-testid={`preset-${preset.id}`}
                >
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{preset.name.replace("Nightmare: ", "")}</CardTitle>
                    <CardDescription className="text-xs line-clamp-2">{preset.context}</CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {preset.scenario_type.replace(/_/g, " ")}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-red-500">
                        {preset.client_personality}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          
          {formData.name && (
            <div className="sticky bottom-4 flex justify-end pt-4">
              <Button 
                onClick={handleStartRoleplay} 
                disabled={startingSession}
                size="lg"
                className="bg-primary hover:bg-primary/90 shadow-lg"
                data-testid="start-preset-btn"
              >
                {startingSession ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4 mr-2" />
                    Start: {formData.name}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6 space-y-8">
          {/* My Templates */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bookmark className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">My Templates</h3>
                <p className="text-sm text-muted-foreground">Your saved scenario configurations</p>
              </div>
            </div>
            {myTemplates.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myTemplates.map((tpl) => (
                  <Card
                    key={tpl.template_id}
                    className="cursor-pointer hover:border-primary/50 transition-colors border-l-4 border-l-primary"
                    onClick={() => handleLoadTemplate(tpl)}
                    data-testid={`my-template-${tpl.template_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">
                          {tpl.name}
                        </CardTitle>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); handleToggleShare(tpl.template_id); }} data-testid={`share-template-${tpl.template_id}`}>
                            <Share2 className={`w-3.5 h-3.5 ${tpl.is_shared ? "text-primary" : "text-muted-foreground"}`} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, id: tpl.template_id, name: tpl.name }); }} data-testid={`delete-template-${tpl.template_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                      <CardDescription className="text-xs line-clamp-2">{tpl.context || "No description"}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{tpl.scenario_type?.replace(/_/g, " ")}</Badge>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{tpl.difficulty}</Badge>
                        {tpl.is_shared && <Badge className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary">Shared</Badge>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Bookmark className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <h4 className="font-medium mb-1">No templates yet</h4>
                <p className="text-sm text-muted-foreground">Create a custom scenario and save it as a template</p>
              </Card>
            )}
          </div>

          {/* Team Templates */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg">Team Templates</h3>
                <p className="text-sm text-muted-foreground">Shared by your teammates</p>
              </div>
            </div>
            {teamTemplates.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {teamTemplates.map((tpl) => (
                  <Card
                    key={tpl.template_id}
                    className="cursor-pointer hover:border-blue-500/50 transition-colors border-l-4 border-l-blue-500"
                    onClick={() => handleLoadTemplate(tpl)}
                    data-testid={`team-template-${tpl.template_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-base">{tpl.name}</CardTitle>
                        {(tpl.user_id === user?.user_id || user?.is_admin) && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, id: tpl.template_id, name: tpl.name }); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      <CardDescription className="text-xs">
                        by {tpl.creator_name || "Unknown"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-wrap gap-1.5 text-xs">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{tpl.scenario_type?.replace(/_/g, " ")}</Badge>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{tpl.difficulty}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <h4 className="font-medium mb-1">No team templates</h4>
                <p className="text-sm text-muted-foreground">Share your templates to see them here</p>
              </Card>
            )}
          </div>

          {formData.name && (
            <div className="sticky bottom-4 flex justify-end pt-4">
              <Button onClick={handleStartRoleplay} disabled={startingSession} size="lg" className="bg-primary hover:bg-primary/90 shadow-lg" data-testid="start-template-btn">
                {startingSession ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
                ) : (
                  <><Mic className="w-4 h-4 mr-2" /> Start: {formData.name} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Custom Tab */}
        <TabsContent value="custom" className="mt-6 space-y-6">
          {/* Scenario Name */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                Scenario Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Scenario Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., Cold Call - Tech Startup CEO"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="scenario-name-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Scenario Type</Label>
                  <Select 
                    value={formData.scenario_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, scenario_type: v }))}
                  >
                    <SelectTrigger data-testid="scenario-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCENARIO_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <span>{type.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">
                              {type.desc}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Client Personality</Label>
                  <Select 
                    value={formData.client_personality} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, client_personality: v }))}
                  >
                    <SelectTrigger data-testid="personality-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PERSONALITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Client Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Building className="w-5 h-5 text-primary" />
                Client Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Industry</Label>
                  <Select 
                    value={formData.industry} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, industry: v }))}
                  >
                    <SelectTrigger data-testid="industry-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((i) => (
                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Company Size</Label>
                  <Select 
                    value={formData.company_size} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, company_size: v }))}
                  >
                    <SelectTrigger data-testid="company-size-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Additional Context</Label>
                <Textarea
                  placeholder="Describe the client's situation, background, or any specific details..."
                  value={formData.context}
                  onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value }))}
                  rows={3}
                  data-testid="context-textarea"
                />
              </div>

              {/* Custom AI Character (Optional) */}
              <div className="space-y-2 pt-2">
                <Label className="text-muted-foreground text-sm">Custom AI Character (Optional)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Leave blank to auto-generate, or specify to replicate real-world situations
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">AI Contact Name</Label>
                    <Input
                      placeholder="e.g., John Smith"
                      value={formData.custom_ai_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_ai_name: e.target.value }))}
                      data-testid="custom-ai-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Business Name</Label>
                    <Input
                      placeholder="e.g., ABC Roofing"
                      value={formData.custom_business_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, custom_business_name: e.target.value }))}
                      data-testid="custom-business-name"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Voice & Difficulty Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-primary" />
                Voice & Difficulty
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Voice Selection */}
              <div className="space-y-3">
                <Label>AI Client Voice</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {VOICE_OPTIONS.map((voice) => {
                    const isPlaying = previewVoice === voice.value;
                    const isLoading = previewLoading === voice.value;
                    return (
                      <div
                        key={voice.value}
                        onClick={() => setFormData(prev => ({ ...prev, voice: voice.value }))}
                        className={`relative cursor-pointer rounded-lg border-2 p-3 transition-all hover:border-primary/50 ${
                          formData.voice === voice.value
                            ? "border-primary bg-primary/5"
                            : "border-border"
                        }`}
                        data-testid={`voice-${voice.value}`}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleVoicePreview(voice.value);
                          }}
                          disabled={isLoading}
                          aria-label={isPlaying ? `Stop ${voice.label} preview` : `Preview ${voice.label} voice`}
                          className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center border transition-colors ${
                            isPlaying
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-primary/60"
                          }`}
                          data-testid={`voice-preview-${voice.value}`}
                        >
                          {isLoading ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : isPlaying ? (
                            <Pause className="w-3.5 h-3.5" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <div className="flex items-center gap-2 pr-7">
                          <div className={`w-2 h-2 rounded-full ${
                            voice.gender === "female" ? "bg-pink-400" :
                            voice.gender === "male" ? "bg-blue-400" : "bg-purple-400"
                          }`} />
                          <span className="font-medium text-sm">{voice.label}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{voice.desc}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Difficulty Selection */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Gauge className="w-4 h-4" />
                  Difficulty Level
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  {DIFFICULTY_LEVELS.map((level) => (
                    <div
                      key={level.value}
                      onClick={() => setFormData(prev => ({ ...prev, difficulty: level.value }))}
                      className={`cursor-pointer rounded-lg border-2 p-4 text-center transition-all hover:border-primary/50 ${
                        formData.difficulty === level.value 
                          ? `border-primary ${level.bgColor}` 
                          : "border-border"
                      }`}
                      data-testid={`difficulty-${level.value}`}
                    >
                      <span className={`font-semibold ${level.color}`}>{level.label}</span>
                      <p className="text-xs text-muted-foreground mt-1">{level.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Objections & Goals */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                Objections & Goals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Objections */}
              <div className="space-y-3">
                <Label>Specific Objections to Practice</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., Price is too high"
                    value={newObjection}
                    onChange={(e) => setNewObjection(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddObjection()}
                    data-testid="objection-input"
                  />
                  <Button type="button" variant="secondary" onClick={handleAddObjection}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.specific_objections.map((obj) => (
                    <Badge key={obj} variant="secondary" className="pl-3 pr-1 py-1">
                      {obj}
                      <button
                        onClick={() => handleRemoveObjection(obj)}
                        className="ml-2 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Goals */}
              <div className="space-y-3">
                <Label>Your Goals for This Call</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., Book a discovery call"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAddGoal()}
                    data-testid="goal-input"
                  />
                  <Button type="button" variant="secondary" onClick={handleAddGoal}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.goals.map((goal) => (
                    <Badge key={goal} variant="outline" className="pl-3 pr-1 py-1">
                      {goal}
                      <button
                        onClick={() => handleRemoveGoal(goal)}
                        className="ml-2 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Screen Sharing Mode & Start Button */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Screen Sharing Mode Toggle */}
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Monitor className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <Label htmlFor="screen-sharing-mode" className="font-medium cursor-pointer">
                      Screen Sharing Mode
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Enable if sharing your screen on a video call (uses push-to-talk)
                    </p>
                  </div>
                  <Switch
                    id="screen-sharing-mode"
                    checked={screenSharingMode}
                    onCheckedChange={setScreenSharingMode}
                    data-testid="screen-sharing-toggle"
                  />
                </div>

                {/* Start Button */}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSaveAsTemplate}
                    disabled={!formData.name.trim() || savingTemplate}
                    data-testid="save-template-btn"
                  >
                    {savingTemplate ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Template
                  </Button>
                  <Button 
                    onClick={handleStartRoleplay} 
                    disabled={!formData.name.trim() || startingSession}
                    size="lg"
                    className="bg-primary hover:bg-primary/90 shadow-lg"
                    data-testid="start-custom-btn"
                  >
                  {startingSession ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Starting Session...
                    </>
                  ) : (
                    <>
                      <Mic className="w-4 h-4 mr-2" />
                      Start Roleplay
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Template Dialog - outside Tabs so it renders on any tab */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &ldquo;{deleteDialog.name}&rdquo;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTemplate} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" /> Delete</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GuidedTour
        steps={ROLEPLAY_TOUR_STEPS}
        isOpen={showTour}
        onComplete={() => setShowTour(false)}
        tourKey={ROLEPLAY_TOUR_KEY}
        onBeforeStep={handleBeforeStep}
      />
    </div>
  );
}
